import * as React from "react";
import { CalendarCheck, X } from "lucide-react";
import { Link } from "react-router-dom";

import { listBlackouts, listChatLog } from "@/api/client";
import { AgentInfo, BlackoutView, ChatLogEntryView, UserEvent } from "@/api/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { calendarLinkFor } from "@/features/meetings/shared";
import { formatDateTime, formatSlotRange } from "@/lib/datetime";
import { cn } from "@/lib/utils";

import { slotsFromRaw, summarizeA2a } from "./a2aSlots";
import { OverlapTimeline } from "./OverlapTimeline";

/**
 * Side drawer showing the full chronological log for a single conversation:
 * user messages, agent replies, and every A2A wire event.
 *
 * <p>Initial fetch hits {@code /api/v1/chat-log?conversationId=…} on open.
 * After that, the parent ({@link import("../ChatPage")}) feeds in live
 * {@link UserEvent}s for this conversation via the {@code liveEvents}
 * prop. Each new event triggers a re-fetch so the merged USER_MSG /
 * AGENT_REPLY / A2A_EVENT rows reflect the just-persisted state.</p>
 *
 * <p>Re-fetching (rather than appending client-side) keeps the drawer
 * the single source of truth for ordering + dedup — every event the
 * backend cares about ends up persisted in chat_message or
 * negotiation_message, so reading them back is correct by construction.
 * The loading spinner only flashes on the initial open; subsequent
 * refreshes are silent.</p>
 */
export function ConversationDetailDrawer({
  conversationId,
  liveEvents,
  chain,
  onNavigate,
  onClose,
}: {
  conversationId: string | null;
  /** Live events for this conversation; new entries trigger a re-fetch of /chat-log. */
  liveEvents?: UserEvent[];
  /**
   * Neighbours of this negotiation in its reschedule chain, so the drawer can walk
   * a meeting's history. Null ids simply hide the link.
   */
  chain?: { previousId: string | null; nextId: string | null };
  /** Switch the drawer to another negotiation (used by the chain links). */
  onNavigate?: (conversationId: string) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = React.useState<ChatLogEntryView[]>([]);
  const [blackouts, setBlackouts] = React.useState<BlackoutView[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Re-fetch when the conversation changes OR when a new live event arrives
  // for it. liveEvents.length is a cheap monotonically-increasing key.
  const liveCount = liveEvents?.length ?? 0;

  React.useEffect(() => {
    if (!conversationId) return;
    // Only show the spinner on first load for this conversation, not on
    // every live-event refresh.
    setLoading((prev) => (rows.length === 0 ? true : prev));
    let cancelled = false;
    // Scope server-side. Fetching the caller's most-recent 200 GLOBAL entries and
    // filtering here missed any conversation buried past that window — a meeting a
    // few days old with 200+ newer entries since read as empty. The scoped read
    // filters before truncation, so the thread's own events always come back.
    listChatLog(undefined, 200, conversationId)
      .then((all) => {
        if (cancelled) return;
        setRows(all.slice().reverse());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rows.length
    // is read but intentionally not a dep: we only check it to decide the
    // spinner flash, not to retrigger the fetch.
  }, [conversationId, liveCount]);

  // The times THIS user has ruled out on the chain. Fetched separately: it's a
  // different resource with a different lifetime, and a failure here must not
  // blank the log.
  React.useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    listBlackouts(conversationId)
      .then((b) => {
        if (!cancelled) setBlackouts(b);
      })
      .catch(() => {
        if (!cancelled) setBlackouts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, liveCount]);

  // When the drawer reopens on a new conversation, wipe stale rows so the
  // spinner shows immediately instead of the previous conv's content.
  React.useEffect(() => {
    setRows([]);
    setBlackouts([]);
  }, [conversationId]);

  const open = conversationId != null;

  // The agreed slot, read off the ACCEPT wire event — the same source the log
  // rows render, so the footer can never disagree with what is above it.
  const bookedRange = React.useMemo(() => {
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i];
      if (row.kind !== "A2A_EVENT") continue;
      if (row.payload.message_type.toLowerCase() !== "accept") continue;
      const slot = slotsFromRaw(row.payload.raw)[0];
      if (slot) return formatSlotRange(slot.start, slot.end);
    }
    return null;
  }, [rows]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/30 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        aria-hidden={!open}
      />
      {/* Drawer */}
      <aside
        className={cn(
          "fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l-4 border-brand-coral bg-card shadow-2xl transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
        aria-hidden={!open}
      >
        <header className="flex items-center justify-between border-b border-border bg-muted px-4 py-3">
          <div>
            <div className="font-comic-headline text-xs uppercase text-muted-foreground">
              Negotiation
            </div>
            {/* The full id, wrapped rather than clipped: the trailing "…" read as
                truncation because it WAS truncation, and a partial id is useless
                for grepping logs or filing a bug. */}
            <div className="break-all font-mono text-xs leading-snug text-foreground">
              {conversationId ? `#${conversationId}` : ""}
            </div>
            {(chain?.previousId || chain?.nextId) && onNavigate && (
              <div className="mt-1 flex flex-col gap-0.5">
                {chain.previousId && (
                  <button
                    type="button"
                    onClick={() => onNavigate(chain.previousId!)}
                    className="w-fit font-mono text-xs text-brand-blue underline underline-offset-2 hover:no-underline"
                  >
                    ← rescheduled from #{chain.previousId.slice(0, 8)}
                  </button>
                )}
                {chain.nextId && (
                  <button
                    type="button"
                    onClick={() => onNavigate(chain.nextId!)}
                    className="w-fit font-mono text-xs text-brand-blue underline underline-offset-2 hover:no-underline"
                  >
                    → replaced by #{chain.nextId.slice(0, 8)}
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border bg-card p-1 transition hover:bg-brand-blue/10 hover:text-brand-blue"
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <ScrollArea className="flex-1 p-4">
          {loading && (
            <p className="text-sm italic text-muted-foreground">Loading…</p>
          )}
          {!loading && rows.length === 0 && (
            <p className="text-sm italic text-muted-foreground">
              No events recorded for this conversation yet.
            </p>
          )}
          {!loading && rows.length > 0 && <OverlapTimeline rows={rows} />}

          {blackouts.length > 0 && (
            <div className="mb-3 rounded border border-dashed border-border bg-muted/40 p-2">
              <div className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
                You rescheduled from
              </div>
              <ul className="mt-1 space-y-0.5">
                {blackouts.map((b, i) => (
                  <li key={i} className="font-mono text-sm text-muted-foreground line-through">
                    {formatSlotRange(b.start, b.end)}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Your agent won't book these again on this meeting. Only you see them.
              </p>
            </div>
          )}

          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="rounded border border-border bg-card p-2 text-xs">
                <DetailRow row={r} />
              </li>
            ))}
          </ul>
        </ScrollArea>

        {bookedRange && conversationId && (
          <footer className="border-t border-border bg-brand-blue/10 px-4 py-3">
            <div className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground">
              Booked
            </div>
            <Link
              to={calendarLinkFor(conversationId)}
              className="mt-0.5 inline-flex items-center gap-1.5 font-mono text-sm text-brand-blue underline underline-offset-2 hover:no-underline"
            >
              <CalendarCheck className="h-3.5 w-3.5" />
              {bookedRange}
            </Link>
          </footer>
        )}
      </aside>
    </>
  );
}

function DetailRow({ row }: { row: ChatLogEntryView }) {
  const at = formatDateTime(row.occurred_at);
  if (row.kind === "USER_MSG") {
    return (
      <>
        <Tag label="YOU → AGENT" tone="you" />
        <div className="mt-1 text-sm text-foreground">{row.payload.text}</div>
        <div className="mt-1 text-xs text-muted-foreground">{at}</div>
      </>
    );
  }
  if (row.kind === "AGENT_REPLY") {
    return (
      <>
        <Tag label="AGENT → YOU" tone="agent" />
        <div className="mt-1 text-sm text-foreground">{row.payload.text}</div>
        <div className="mt-1 text-xs text-muted-foreground">{at}</div>
      </>
    );
  }
  if (row.kind === "COHORT_PROPOSAL") {
    // The drawer is a single negotiation's wire trace, and a cohort proposal belongs to no
    // negotiation: it is a set of cards the user answers in the main chat. A proposal cannot
    // reach here (the drawer is scoped by conversation id and these rows carry none), but the
    // union makes the case explicit rather than letting it fall through into the A2A branch.
    return null;
  }
  // A2A_EVENT. `direction` is from THIS user's agent perspective (OUTBOUND = we
  // sent it, INBOUND = we received it — see NegotiationMessageRepository
  // .findVisibleBefore). Pin our agent on the left so the name never swaps sides
  // between a request and its response, and let the arrow carry the flow:
  // `→` we sent, `←` we received.
  const { message_type, direction, from, to, round, raw } = row.payload;
  const outbound = direction === "OUTBOUND";
  const mine = outbound ? from : to;
  const peer = outbound ? to : from;
  const arrow = outbound ? "→" : "←";
  const summary = summarizeA2a(message_type, raw);
  return (
    <>
      <Tag
        label={`A2A · ${message_type} · ROUND ${round}`}
        tone={direction === "OUTBOUND" ? "outbound" : "inbound"}
      />
      <div className="mt-1 font-mono text-sm text-foreground">
        {labelFor(mine)} {arrow} {labelFor(peer)}
      </div>
      {summary.slots.length > 0 && (
        <div className="mt-1.5">
          <div className="font-comic-headline text-xs uppercase text-muted-foreground">
            {summary.label}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {summary.slots.map((slot, i) => (
              <li key={i} className="font-mono text-sm text-foreground">
                • {slot}
              </li>
            ))}
          </ul>
        </div>
      )}
      {summary.note && (
        <div className="mt-1 text-sm italic text-foreground">
          {summary.label}: {summary.note}
        </div>
      )}
      <div className="mt-1 text-xs text-muted-foreground">{at}</div>
    </>
  );
}

function Tag({
  label,
  tone,
}: {
  label: string;
  /** Who the row belongs to. Rotates blue → teal → coral → yellow across the peers. */
  tone: "you" | "agent" | "outbound" | "inbound";
}) {
  const bg = {
    you: "bg-brand-blue/15",
    agent: "bg-brand-teal/15",
    outbound: "bg-brand-yellow/25",
    inbound: "bg-brand-coral/15",
  }[tone];
  return (
    <span
      className={cn(
        "inline-block rounded border border-border px-1.5 py-0.5 font-comic-headline text-xs uppercase text-foreground",
        bg,
      )}
    >
      {label}
    </span>
  );
}

function labelFor(agent: AgentInfo | null): string {
  if (!agent) return "unknown";
  if (agent.handle) return `@${agent.handle}`;
  if (agent.displayName) return agent.displayName;
  return agent.id.slice(0, 8);
}
